import type { Messenger, CallbackFunction } from '../interfaces/messenger'

// Simple event emitter for web version - this should be the same as the shared one
class WebMessenger implements Messenger {
  private listeners: Map<string, Set<CallbackFunction<any, any>>> = new Map()
  
  available = true
  name = 'web-messenger'
  
  async send<TPayload, TResponse>(
    topic: string,
    payload: TPayload,
    options?: { id?: string | number }
  ): Promise<TResponse> {
    const fullTopic = options?.id ? `${topic}:${options.id}` : topic
    console.log('[Plug-Core Messenger] Sending message to topic:', fullTopic, 'payload:', payload)
    
    return new Promise((resolve, reject) => {
      // First try with ID-specific topic
      let listeners = this.listeners.get(fullTopic)
      
      // If no ID-specific listener, try the base topic
      if ((!listeners || listeners.size === 0) && options?.id) {
        console.log('[Plug-Core Messenger] No listeners for', fullTopic, 'trying base topic:', topic)
        listeners = this.listeners.get(topic)
      }
      
      console.log('[Plug-Core Messenger] Available topics:', Array.from(this.listeners.keys()))
      console.log('[Plug-Core Messenger] Listeners for topic:', listeners?.size || 0)
      
      if (!listeners || listeners.size === 0) {
        reject(new Error(`No handler for topic: ${topic}`))
        return
      }
      
      // Call the first listener (in web, we typically have one handler per topic)
      const listener = Array.from(listeners)[0]
      
      listener(payload, {
        sender: { origin: window.location.origin }
      })
        .then(resolve)
        .catch(reject)
    })
  }
  
  reply<TPayload, TResponse>(
    topic: string,
    callback: CallbackFunction<TPayload, TResponse>
  ): () => void {
    console.log('[Plug-Core Messenger] Registering reply handler for topic:', topic)
    if (!this.listeners.has(topic)) {
      this.listeners.set(topic, new Set())
    }
    
    this.listeners.get(topic)!.add(callback)
    console.log('[Plug-Core Messenger] Total listeners for', topic, ':', this.listeners.get(topic)!.size)
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(topic)
      if (listeners) {
        listeners.delete(callback)
        if (listeners.size === 0) {
          this.listeners.delete(topic)
        }
      }
    }
  }
}

// Global singleton - this needs to be the SAME instance used everywhere
declare global {
  var __globalWebMessenger: WebMessenger | undefined
}

if (!globalThis.__globalWebMessenger) {
  globalThis.__globalWebMessenger = new WebMessenger()
  console.log('[Plug-Core Messenger] Created global singleton instance')
}

// Web version doesn't use detectScriptType - all connections use the same messenger
type InitializeMessengerArgs = {
  connect: string
}

export const initializeMessenger = ({ connect }: InitializeMessengerArgs): Messenger => {
  console.log('[Plug-Core Messenger] Initializing for connection:', connect, '- using global singleton')
  return globalThis.__globalWebMessenger!
}